import * as Popover from '@radix-ui/react-popover';
import { useQuery } from '@tanstack/react-query';
import { Check, ChevronsUpDown, Loader2, X } from 'lucide-react';
import { useEffect, useId, useMemo, useRef, useState, type KeyboardEvent } from 'react';
import { get, type ApiError } from '../lib/api';
import { cn } from '../lib/cn';
import { Avatar, Badge } from './ui';

/**
 * Choosing a person.
 *
 * Several screens needed one and each improvised: a merge that asked for a raw
 * account id nobody could see, pickers that rendered blank because they read
 * `full_name` from an endpoint that sends `name`, and catalogue questions of
 * type "a person" that were a text box accepting a number. This is the one
 * picker: type to search, arrow to choose, Enter to pick, and the person shown
 * by name and email so two people called Sam can be told apart.
 *
 * `agents` searches `/directory/agents`, open to anyone signed in. `users`
 * searches `/directory/users`, which needs `user.view` — a screen offering it to
 * somebody without that permission will get an error state, not a leak.
 */

export interface PersonOption {
  id: number;
  name: string;
  email?: string | null;
  is_agent?: boolean;
  is_vip?: boolean;
  is_available?: boolean | null;
  open_tickets?: number | null;
  department?: string | null;
  job_title?: string | null;
}

export interface PersonPickerProps {
  source: 'agents' | 'users';
  /** The chosen person, or null. Pass the object so the name shows without a lookup. */
  value: PersonOption | null;
  onChange: (person: PersonOption | null) => void;
  /** Limit agents to one group's members. */
  groupId?: number | null;
  /** People who may not be chosen — the record being merged, the actor themselves. */
  exclude?: number[];
  placeholder?: string;
  /** Offer a way back to "nobody", for an optional field such as an assignee. */
  clearable?: boolean;
  clearLabel?: string;
  disabled?: boolean;
  id?: string;
  'aria-label'?: string;
  'aria-describedby'?: string;
  'aria-invalid'?: boolean;
  size?: 'sm' | 'md';
  className?: string;
}

function useDebounced<T>(value: T, delay: number): T {
  const [debounced, setDebounced] = useState(value);
  useEffect(() => {
    const timer = window.setTimeout(() => setDebounced(value), delay);
    return () => window.clearTimeout(timer);
  }, [value, delay]);
  return debounced;
}

export function PersonPicker({
  source,
  value,
  onChange,
  groupId,
  exclude = [],
  placeholder = 'Search by name or email…',
  clearable = false,
  clearLabel = 'Nobody',
  disabled,
  id,
  size = 'md',
  className,
  ...aria
}: PersonPickerProps) {
  const [open, setOpen] = useState(false);
  const [query, setQuery] = useState('');
  const [active, setActive] = useState(0);
  const inputRef = useRef<HTMLInputElement>(null);
  const listId = useId();
  const debounced = useDebounced(query.trim(), 180);

  const people = useQuery<{ data: PersonOption[] }, ApiError>({
    queryKey: ['person-picker', source, groupId ?? null, debounced],
    queryFn: () =>
      get(`/directory/${source}`, {
        ...(debounced ? { q: debounced } : {}),
        ...(source === 'agents' && groupId ? { group_id: groupId } : {}),
      }),
    enabled: open,
    staleTime: 30_000,
  });

  const options = useMemo(() => {
    const excluded = new Set(exclude);
    return (people.data?.data ?? []).filter((person) => !excluded.has(person.id)).slice(0, 50);
  }, [people.data, exclude]);

  // Rows are the clear option (when offered) followed by the people.
  const rows: Array<PersonOption | null> = clearable ? [null, ...options] : options;

  useEffect(() => {
    setActive(0);
  }, [debounced, open]);

  const choose = (person: PersonOption | null) => {
    onChange(person);
    setOpen(false);
    setQuery('');
  };

  const onKeyDown = (event: KeyboardEvent<HTMLInputElement>) => {
    if (event.key === 'ArrowDown') {
      event.preventDefault();
      if (!open) setOpen(true);
      else setActive((current) => Math.min(current + 1, Math.max(rows.length - 1, 0)));
    } else if (event.key === 'ArrowUp') {
      event.preventDefault();
      setActive((current) => Math.max(current - 1, 0));
    } else if (event.key === 'Enter') {
      if (!open) return;
      event.preventDefault();
      if (rows.length > 0) choose(rows[active] ?? null);
    } else if (event.key === 'Escape' && open) {
      event.preventDefault();
      setOpen(false);
    }
  };

  const height = size === 'sm' ? 'h-7 text-mini' : 'h-8 text-body';
  const activeId = open && rows.length > 0 ? `${listId}-${active}` : undefined;

  return (
    <Popover.Root open={open} onOpenChange={setOpen}>
      <Popover.Anchor asChild>
        <div
          className={cn(
            'relative flex w-full items-center rounded-control border border-line-strong bg-surface',
            'focus-within:outline-2 focus-within:outline-offset-1 focus-within:outline-accent',
            aria['aria-invalid'] && 'border-critical ring-1 ring-critical',
            disabled && 'opacity-60',
            height,
            className,
          )}
        >
          {!open && value && (
            <span className="pointer-events-none flex min-w-0 flex-1 items-center gap-2 ps-2">
              <Avatar name={value.name} size="xs" />
              <span dir="auto" className="truncate text-ink">
                {value.name}
              </span>
            </span>
          )}
          <input
            ref={inputRef}
            id={id}
            role="combobox"
            aria-expanded={open}
            aria-controls={listId}
            aria-activedescendant={activeId}
            aria-autocomplete="list"
            aria-label={aria['aria-label']}
            aria-describedby={aria['aria-describedby']}
            aria-invalid={aria['aria-invalid'] || undefined}
            disabled={disabled}
            dir="auto"
            value={query}
            placeholder={value ? '' : placeholder}
            onChange={(event) => {
              setQuery(event.target.value);
              if (!open) setOpen(true);
            }}
            onFocus={() => setOpen(true)}
            onClick={() => setOpen(true)}
            onKeyDown={onKeyDown}
            className={cn(
              'h-full min-w-0 bg-transparent px-2.5 text-ink outline-none placeholder:text-ink-subtle',
              !open && value ? 'absolute inset-0 opacity-0' : 'flex-1',
            )}
          />
          {value && clearable && !disabled && !open ? (
            <button
              type="button"
              aria-label={`Clear ${value.name}`}
              onClick={() => onChange(null)}
              className="relative z-10 me-1 rounded p-1 text-ink-subtle hover:bg-sunken hover:text-ink"
            >
              <X aria-hidden className="size-3.5" />
            </button>
          ) : (
            <ChevronsUpDown aria-hidden className="pointer-events-none me-2 size-3.5 shrink-0 text-ink-subtle" />
          )}
        </div>
      </Popover.Anchor>

      <Popover.Portal>
        <Popover.Content
          align="start"
          sideOffset={4}
          // Focus stays in the input: this is a combobox, not a menu.
          onOpenAutoFocus={(event) => event.preventDefault()}
          onInteractOutside={(event) => {
            if (event.target instanceof Node && inputRef.current?.parentElement?.contains(event.target)) {
              event.preventDefault();
            }
          }}
          className="animate-pop z-[70] max-h-72 w-[var(--radix-popover-trigger-width)] min-w-64 overflow-y-auto rounded-card border border-line bg-raised p-1 shadow-float"
        >
          <div id={listId} role="listbox" aria-label="People">
            {people.isLoading && (
              <p className="flex items-center gap-2 px-2 py-2 text-mini text-ink-subtle">
                <Loader2 aria-hidden className="size-3.5 animate-spin" /> Searching…
              </p>
            )}
            {people.isError && (
              <p role="alert" className="px-2 py-2 text-mini text-critical-ink">
                {people.error.status === 403 ? 'You can’t look people up here.' : 'Couldn’t load people.'}
              </p>
            )}
            {rows.map((person, index) => {
              const selected = person ? value?.id === person.id : value === null;
              return (
                <div
                  key={person?.id ?? 'none'}
                  id={`${listId}-${index}`}
                  role="option"
                  aria-selected={index === active}
                  onMouseMove={() => setActive(index)}
                  onMouseDown={(event) => event.preventDefault()}
                  onClick={() => choose(person)}
                  className={cn(
                    'flex cursor-pointer items-center gap-2 rounded px-2 py-1.5',
                    index === active ? 'bg-sunken' : '',
                  )}
                >
                  {person ? (
                    <>
                      <Avatar name={person.name} size="sm" />
                      <span className="min-w-0 flex-1">
                        <span className="flex items-center gap-1.5">
                          <span dir="auto" className="truncate text-body text-ink">
                            {person.name}
                          </span>
                          {person.is_vip && <Badge tone="caution">VIP</Badge>}
                          {source === 'users' && person.is_agent && <Badge tone="accent">Agent</Badge>}
                          {source === 'agents' && person.is_available === false && <Badge>Away</Badge>}
                        </span>
                        <span className="block truncate text-micro text-ink-subtle">
                          {[person.email, person.department ?? person.job_title].filter(Boolean).join(' · ')}
                          {source === 'agents' && typeof person.open_tickets === 'number'
                            ? ` · ${person.open_tickets} open`
                            : ''}
                        </span>
                      </span>
                    </>
                  ) : (
                    <span className="flex-1 ps-1 text-body text-ink-muted">{clearLabel}</span>
                  )}
                  {selected && <Check aria-hidden className="size-3.5 shrink-0 text-accent" />}
                </div>
              );
            })}
            {!people.isLoading && !people.isError && options.length === 0 && (
              <p className="px-2 py-2 text-mini text-ink-subtle">
                {debounced ? 'Nobody matches that.' : 'Start typing a name or email.'}
              </p>
            )}
          </div>
        </Popover.Content>
      </Popover.Portal>
    </Popover.Root>
  );
}
